<script setup lang="ts">
import FinestCard from '@/components/FinestCard.vue'
import FinestListItem from '@/components/FinestListItem.vue'
import PropertyStepErrors from '@/components/portfolio/PropertyStepErrors.vue'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import PersonType = App.Enums.PersonType

export interface PartyOption {
  id: string
  name: string
  type: PersonType
  is_self: boolean
}

export type PartyRole = 'borrower' | 'owner'

const props = withDefaults(
  defineProps<{
    options: PartyOption[]
    selectedId: string | null
    errors: string[]
    role?: PartyRole
  }>(),
  { role: 'borrower' },
)

defineEmits<{
  select: [option: PartyOption]
  addCompany: []
}>()

const { t, te } = useI18n()

const notice = computed(() => (te(`roles.${props.role}.notice`) ? t(`roles.${props.role}.notice`) : null))

const naturalPersons = computed(() =>
  props.options
    .filter((option) => option.type !== 'company')
    .map((option) => ({
      option,
      sublabel: option.is_self ? t('kinds.self') : t('kinds.household'),
    })),
)

const companies = computed(() => props.options.filter((option) => option.type === 'company'))
</script>

<template>
  <FinestCard :title="t(`roles.${role}.title`)" icon="pi pi-user">
    <p class="text-ink-2 mb-4 text-sm">{{ t(`roles.${role}.hint`) }}</p>

    <PropertyStepErrors :messages="errors" class="mb-4" />

    <Message v-if="notice" severity="info" icon="pi pi-info-circle" size="small" class="mb-4">
      <span class="text-sm">{{ notice }}</span>
    </Message>

    <section v-if="naturalPersons.length" class="mb-6">
      <h3 class="text-ink-3 mb-2 text-xs font-semibold tracking-wide uppercase">{{ t('groups.naturalPersons') }}</h3>

      <div class="grid gap-2 sm:grid-cols-2">
        <FinestListItem
          v-for="entry in naturalPersons"
          :key="entry.option.id"
          icon="pi pi-user"
          :label="entry.option.name"
          :sublabel="entry.sublabel"
          :selected="entry.option.id === selectedId"
          :avatar-gradient="entry.option.is_self"
          @click="$emit('select', entry.option)" />
      </div>
    </section>

    <section>
      <h3 class="text-ink-3 mb-2 text-xs font-semibold tracking-wide uppercase">{{ t('groups.companies') }}</h3>

      <div class="grid gap-2 sm:grid-cols-2">
        <FinestListItem
          v-for="company in companies"
          :key="company.id"
          icon="pi pi-building"
          :label="company.name"
          :selected="company.id === selectedId"
          @click="$emit('select', company)" />

        <button
          type="button"
          class="border-line text-ink-3 hover:border-action hover:bg-info-subtle hover:text-action flex cursor-pointer items-center gap-2.5 rounded-[10px] border border-dashed px-3 py-2.5 text-left transition-colors"
          @click="$emit('addCompany')">
          <span
            class="flex h-8 w-8 flex-none items-center justify-center rounded-full border border-dashed border-current">
            <i class="pi pi-plus text-xs" aria-hidden="true" />
          </span>

          <span class="min-w-0 flex-1">
            <span class="block truncate text-sm font-medium">{{ t('addCompany.label') }}</span>
            <span class="text-ink-3 block truncate text-xs">{{ t('addCompany.sublabel') }}</span>
          </span>
        </button>
      </div>
    </section>
  </FinestCard>
</template>

<i18n>
{
  "de": {
    "roles": {
      "borrower": {
        "title": "Wer ist der Kreditnehmer?",
        "hint": "Der Kreditnehmer wird auch als Eigentümer des neuen Objekts hinterlegt. Du kannst das später an der Immobilie ändern.",
        "notice": "Nur beim Ankauf nötig. Bei einer Portfolio-Immobilie steht der Eigentümer bereits fest."
      },
      "owner": {
        "title": "Wem gehört die Immobilie?",
        "hint": "Der Eigentümer ist später auch der Kreditnehmer, wenn du die Immobilie finanzierst. Du kannst ihn an der Immobilie jederzeit ändern."
      }
    },
    "groups": {
      "naturalPersons": "Natürliche Personen",
      "companies": "Unternehmen"
    },
    "kinds": {
      "self": "Du selbst",
      "household": "Haushalt"
    },
    "addCompany": {
      "label": "Unternehmen hinzufügen",
      "sublabel": "Nur der Name genügt"
    }
  },
  "en": {
    "roles": {
      "borrower": {
        "title": "Who is the borrower?",
        "hint": "The borrower is also recorded as the owner of the new property. You can change that later on the property.",
        "notice": "Only needed for a purchase. For a portfolio property the owner is already set."
      },
      "owner": {
        "title": "Who owns the property?",
        "hint": "The owner is also the borrower once you finance the property. You can change it on the property at any time."
      }
    },
    "groups": {
      "naturalPersons": "Natural persons",
      "companies": "Companies"
    },
    "kinds": {
      "self": "Yourself",
      "household": "Household"
    },
    "addCompany": {
      "label": "Add company",
      "sublabel": "The name is enough"
    }
  }
}
</i18n>
